import { useCallback, useState } from "react";
import { SBSortingOrder } from "@bookingcom/flights-searchbox";
import { StoreState } from "../store";
import { useHistory } from "react-router-dom";
import { UIFlightTimeItemAgg } from "@flights/types";
import deepEqual from "deep-equal";
import { useApplyFilters } from "../components/elements/SearchResults/components/boundsFilterHooks";
import { useSelector } from "react-redux";
import { getSearch } from "../store/search/selectors";

type FilterState = StoreState["search"]["filters"];

export function getUpdatedSearchParams(filters: FilterState, resetPage: boolean = true) {
  const qs = new URLSearchParams(location.search);

  if (filters.stops != undefined) {
    qs.set("stops", "" + filters.stops);
  } else {
    qs.delete("stops");
  }

  if (filters.airlines && filters.airlines.length > 0) {
    qs.set("airlines", filters.airlines.join(","));
  } else {
    qs.delete("airlines");
  }

  if (filters.duration) {
    qs.set("duration", `${filters.duration}`);
  } else {
    qs.delete("duration");
  }

  if (filters.departureIntervals) {
    qs.delete("depInt");
    filters.departureIntervals.forEach((interval) => {
      qs.append("depInt", interval.join("-"));
    });
  } else {
    qs.delete("depInt");
  }

  if (filters.arrivalIntervals) {
    qs.delete("arrInt");
    filters.arrivalIntervals.forEach((interval) => {
      qs.append("arrInt", interval.join("-"));
    });
  } else {
    qs.delete("arrInt");
  }

  if (filters.flightTimes?.length) {
    qs.delete("depTimeInt");
    qs.delete("arrTimeInt");
    let departures: string = "";
    let arrivals: string = "";
    filters.flightTimes.forEach((interval) => {
      const currentDeparture = getflightTimeIntervalSelection(interval.departure);
      const currentArrival = getflightTimeIntervalSelection(interval.arrival);
      departures = departures.length === 0 ? `${currentDeparture}` : `${departures}|${currentDeparture}`;
      arrivals = arrivals.length === 0 ? `${currentArrival}` : `${arrivals}|${currentArrival}`;
    });
    qs.append("depTimeInt", departures);
    qs.append("arrTimeInt", arrivals);
  } else {
    qs.delete("depTimeInt");
    qs.delete("arrTimeInt");
  }

  if (filters.shortLayoverConnection) {
    qs.delete("shortLayoverConnection");
    qs.append("shortLayoverConnection", String("1"));
  } else {
    qs.delete("shortLayoverConnection");
  }

  if (!!filters.includedBaggage?.length) {
    qs.delete("includedBaggage");
    qs.append("includedBaggage", filters.includedBaggage.join(","));
  } else {
    qs.delete("includedBaggage");
  }

  if (!!filters.maxBudget) {
    qs.delete("maxBudget");
    qs.append("maxBudget", String(filters.maxBudget));
  } else {
    qs.delete("maxBudget");
  }

  if (!!filters.maxBudgetPerAdult) {
    qs.delete("maxBudgetPerAdult");
    qs.append("maxBudgetPerAdult", String(filters.maxBudgetPerAdult));
  } else {
    qs.delete("maxBudgetPerAdult");
  }

  if (!!filters.airports?.length) {
    qs.delete("airports");
    qs.append("airports", filters.airports.join(","));
  } else {
    qs.delete("airports");
  }

  if (!!filters.preferSameAirport) {
    qs.delete("preferSameAirport");
    qs.append("preferSameAirport", filters.preferSameAirport);
  } else {
    qs.delete("preferSameAirport");
  }

  if (filters.allDeals) {
    qs.delete("showDeals");
    qs.append("showDeals", String(1));
  } else {
    qs.delete("showDeals");
  }

  if (!!filters.maxLayoverDuration) {
    qs.delete("maxLayoverDuration");
    qs.append("maxLayoverDuration", String(filters.maxLayoverDuration));
  } else {
    qs.delete("maxLayoverDuration");
  }

  if (resetPage) {
    qs.delete("page");
  }

  if (filters.flexibility?.length) {
    qs.set("flexibility", filters.flexibility.join(","));
  } else {
    qs.delete("flexibility");
  }

  return qs;
}

const getflightTimeIntervalSelection = (interval: UIFlightTimeItemAgg[]): string => {
  const selectedIntervals = interval.filter((inter) => inter.selected);
  return selectedIntervals.length > 0
    ? selectedIntervals
        .map((selectedInterval) => {
          const start = selectedInterval.start?.split(":")[0];
          const end = selectedInterval.end?.split(":")[0];
          return `${start}-${end}`;
        })
        .join(",")
    : "00-23"; //If no intervals are selected we return the full duration.
};

const useSearchFilters = () => {
  const history = useHistory();
  const search = useSelector(getSearch);

  // Because the filters could be reset using "Reset all" button, we aren't able to track those store changes.
  // Keeping a copy in the local state helps us to know if filters were reset.
  const [currentFilters] = useState<FilterState>(search.filters);
  const applyFiltersBounds = useApplyFilters();

  const updateUrl = useCallback(
    (query: URLSearchParams) => {
      history.replace(
        {
          pathname: location.pathname,
          search: query.toString()
        },
        { hideLoader: true }
      );
    },
    [history]
  );

  const applyFilters = useCallback(
    (filters: FilterState) => {
      // Checking if filters were changed to reset user to the 1st page.
      const shouldResetPage = !deepEqual(currentFilters, filters);
      const qs = getUpdatedSearchParams(filters, shouldResetPage);
      applyFiltersBounds();

      updateUrl(qs);
    },
    [currentFilters, applyFiltersBounds, updateUrl]
  );

  const applyFiltersAndSort = useCallback(
    // flights_web_sr_gen_ai_summary_v3_mdot

    (filters: FilterState, sort?: SBSortingOrder) => {
      // Checking if filters were changed to reset user to the 1st page.
      const shouldResetPage = !deepEqual(currentFilters, filters) || Boolean(sort);

      const qs = getUpdatedSearchParams(filters, shouldResetPage);

      // On contrary to filters, we don't preserve sort value draft separately in store,
      // the value is saved to store immediately after UI change =>
      // we calculate if sort was actually changed by user using React state on UI layer consuming this hook =>
      // if `sort` arg is passed it means sort was changed and submitted by user.
      if (sort) {
        qs.set("sort", sort);
      }
      applyFiltersBounds();

      updateUrl(qs);
    },
    [currentFilters, applyFiltersBounds, updateUrl]
  );

  return {
    applyFilters,
    applyFiltersAndSort
  };
};

export default useSearchFilters;
